import { injectable, inject } from 'inversify'
import type { ProgressResolverParams, Resolvable } from '@/services/resolvers/types'
import { SERVICE_TYPES, type RegisteredServices } from '@/core/container/types'
import type ProgressService from '@/services/progressService'
import type {
  TServerSubscriberCallback,
  ServerProgressEventPayload,
  IServerProgressEventsMap,
} from '@/services/transport/serverEvents'
import { withUniqueKeys } from '@/decorators/resolvers/withUniqueKeys'
import { persistent } from '@/decorators/resolvers/persistent'
import type { GetLocator } from '@/core/container/container'
import type DomainBaseService from '@/services/domain/domainBaseService'
import type { Progressable } from '@/services/domain/types'
import type OrmApiRepository from '@/data/repositories/ormApiRepository'
import type { ProgressType } from '@/services/types'
import type Progress from '@/data/models/Progress'

@withUniqueKeys('ProgressResolver')
@persistent()
@injectable()
export default class ProgressResolver implements Resolvable<ProgressResolverParams> {
  private subscriptions: Map<RegisteredServices, TServerSubscriberCallback<keyof IServerProgressEventsMap>> = new Map()

  constructor(
    @inject('GetLocator') private readonly get: GetLocator,
    @inject(SERVICE_TYPES.ProgressService)
    protected readonly progressService: ProgressService,
  ) {}

  public async resolve(params: ProgressResolverParams) {
    for (const serviceId of params.serviceIds) {
      const service = this.getDomainProgressableService(serviceId)
      const progressType = service.getProgressType()

      const subscription = service.subscribeProgress((payload: ServerProgressEventPayload) => {
        const eventProgress = this.progressService.mapServerEventPayload(payload, progressType)

        if (this.progressService.isLastProgress(eventProgress)) {
          service.getPreloader().reloadById(service.getModel(), eventProgress.originalId)
        }

        this.progressService.insertOrUpdateProgress(eventProgress)
      })

      this.subscriptions.set(serviceId, subscription)
    }
  }

  public async unresolve(params: ProgressResolverParams) {
    for (const serviceId of params.serviceIds) {
      const subscription = this.subscriptions.get(serviceId)
      if (subscription) {
        this.getDomainProgressableService(serviceId).unsubscribeProgress(subscription)
        this.subscriptions.delete(serviceId)
      }
    }
  }

  public getResolverKeys(params: ProgressResolverParams) {
    return params.serviceIds
  }

  protected getDomainProgressableService(serviceId: RegisteredServices) {
    return this.get<DomainBaseService<OrmApiRepository> & Progressable>(serviceId)
  }

  protected saveProgress(progress: Omit<Progress, 'id'>) {
    return this.progressService.insertOrUpdateProgress({
      ...progress,
    })
  }

  protected getProgress(progressType: ProgressType, originalId: string) {
    return this.progressService.findProgressOrNull({
      type: progressType,
      originalId,
    })
  }
}
